import { Component, inject, OnInit }                           from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router }                              from '@angular/router';
import { MatError, MatFormField, MatLabel }                    from '@angular/material/form-field';
import { MatInput }                                            from '@angular/material/input';
import { MatSelect }                                           from '@angular/material/select';
import { MatOption }                                           from '@angular/material/core';
import { MatProgressSpinner }                                  from '@angular/material/progress-spinner';

import { InvitationData, ShareAccessService }                 from '../../../../api/share-access/share-access.service';
import { GenericDataGridColumnDef, GenericDataGridComponent } from '../../../../shared/components/generic-data-grid/generic-data-grid.component';
import { InternalRoutes }                                     from '../../../../shared/data/internal-routes';
import { UserService }                                        from '../../../../api/user/user.service';
import { ProductType }                                        from '../../../../api/account-data/account-data';
import { DocumentsHelperUtils }                               from '../../../../shared/utils/documents-helper.utils';
import { PageComponent }                                      from '../../../../shared/components/page/page.component';
import { PageTitleComponent }                                 from '../../../../shared/components/page/title/title.component';
import { PageContentComponent }                               from '../../../../shared/components/page/content/content.component';
import { PinNotificationComponent }                           from '../../../../shared/components/notification/notification.component';
import { CardComponent }                                      from '../../../../shared/components/card/card.component';
import { CardTitleComponent }                                 from '../../../../shared/components/card/title/card-title.component';
import { CardContentComponent }                               from '../../../../shared/components/card/content/card-content.component';
import { CardActionsComponent }                               from '../../../../shared/components/card/actions/card-actions.component';
import { ButtonComponent }                                    from '../../../../shared/components/buttons/button.component';
import { ErrorService }                                       from '../../../../core/error-handling/error.service';
import { PageTitleType }                                      from '../../../../shared/components/page/title/title';

@Component({
  selector: 'pin-share-access',
  templateUrl: './share-access.component.html',
  imports: [PageComponent, PageTitleComponent, PageContentComponent, PinNotificationComponent, ReactiveFormsModule, CardComponent, CardTitleComponent, CardContentComponent, MatFormField, MatLabel, MatInput, MatError, MatSelect, MatOption, GenericDataGridComponent, CardActionsComponent, ButtonComponent, MatProgressSpinner]
})
export class ShareAccessComponent implements OnInit {
  protected readonly PageTitleType = PageTitleType;
  internalRoutes = InternalRoutes;
  accountId: number;
  invitationData: InvitationData;
  selectedAccountOwnerId: number;
  accountOwnerList = [];
  accountList = [];
  selectedAccounts = [];
  matchingAccounts = [];
  processingRequest = false;
  isLoadingSession = true;
  showStatements = false;
  accountAlreadyShared = false;
  displayEmailNotValidError = false;
  invitationDenied = false;
  tryAgainLater = false;
  hasCdarsAccounts = false;
  columnDefinitions: GenericDataGridColumnDef[] = [
    {
      columnDef: 'title',
      header: 'SHARED ACCOUNTS',
      cell: row => row.title,
      cellClass: 'col-top'
    },
    {
      columnDef: 'remove',
      hideHeaderFunction: () => this.selectedAccounts && this.selectedAccounts.length < 2,
      header: 'REMOVE ALL',
      headerCellAction: () => this.removeAllAccounts(),
      headerClass: 'col-top',
      headerLinkIcon: 'cancel',
      headerType: 'actionButton',
      type: 'actionButton',
      disabled: _row => false,
      linkIcon: 'cancel',
      cell: () => 'Remove',
      cellAction: (row) => this.removeAccount(row),
      cellClass: 'col-top ',
      sortable: false
    }
  ];
  displayedColumns = ['title', 'remove'];
  private errorService = inject(ErrorService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  shareAccessForm = this.formBuilder.group(
    {
      email: ['', [Validators.required, Validators.email, Validators.pattern('.+\\.[a-zA-Z]{2,}$')]],
      accountOwner: ['', []],
      account: [{value: '', disabled: true}, []]
    }
  );
  private userService = inject(UserService);
  private shareAccessService = inject(ShareAccessService);

  ngOnInit(): void {
    const queryParamMap = this.route.snapshot.queryParamMap;
    this.accountId = queryParamMap.has('accountId') ? Number(queryParamMap.get('accountId')) : NaN;
    this.selectedAccountOwnerId = undefined;
    this.getSession();
    this.loadUserAccounts();
  }

  getSession() {
    const userSession = this.route.snapshot.data.userSession;
    this.showStatements = DocumentsHelperUtils.showStatements(userSession);
    this.isLoadingSession = false;
  }

  loadAccountsForAccountOwner(selectedAccountOwner): void {
    this.populateAccountsForAccountOwner(selectedAccountOwner.id);
  }

  populateAccountsForAccountOwner(accountOwnerId, withSelection = true) {
    this.matchingAccounts = [];
    if (withSelection) {
      this.selectedAccountOwnerId = accountOwnerId;
      this.shareAccessForm.get('account')?.enable();
    }
    this.accountList.forEach(account => {
      if (this.selectedAccounts.indexOf(account) === -1) {
        if (account.accountType !== ProductType.CDARS) {
          this.addToMatchingAccounts(account, accountOwnerId);
        } else if (!this.hasCdarsAccounts) {
          this.hasCdarsAccounts = true;
        }
      }
    });
    this.matchingAccounts.sort((a1, a2) => a1.title.localeCompare(a2.title));
  }

  prepopulateAccountOwnersDropdown(): void {
    if (this.accountOwnerList.length === 1) {
      this.shareAccessForm.controls['accountOwner'].setValue(this.accountOwnerList[0]);
      this.populateAccountsForAccountOwner(this.accountOwnerList[0].id);
    }
  }

  selectAccounts(selectedValue): void {
    if (selectedValue instanceof Array) {
      this.selectedAccounts = this.selectedAccounts.concat(this.matchingAccounts);
      this.matchingAccounts = [];
    } else {
      this.selectAccount(selectedValue);
    }
  }

  selectAccount(account): void {
    const index = this.matchingAccounts.indexOf(account);
    if (index > -1) {
      this.matchingAccounts.splice(index, 1);
      if (this.selectedAccounts.indexOf(account) === -1) {
        this.selectedAccounts = [...this.selectedAccounts, account];
      }
    }
  }

  removeAccount(account): void {
    this.selectedAccounts = this.selectedAccounts.filter(selectedAccount => selectedAccount !== account);
    this.addToMatchingAccounts(account, this.selectedAccountOwnerId);
    this.shareAccessForm.get('account')?.reset();
  }

  removeAllAccounts(): void {
    this.selectedAccounts.forEach(account => {
      this.addToMatchingAccounts(account, this.selectedAccountOwnerId);
    });
    this.selectedAccounts = [];
    this.shareAccessForm.get('account')?.reset();
  }

  addToMatchingAccounts(account, accountOwnerId) {
    Object.keys(account.accountOwners).forEach(key => {
      if (accountOwnerId === Number(key)) {
        this.matchingAccounts.push(account);
      }
    });
  }

  confirmAndShareAccess(): void {
    if (this.isDataValid() && !this.processingRequest) {
      this.processingRequest = true;
      this.invitationData = {
        agentEmail: this.shareAccessForm.getRawValue()['email'],
        accountIds: this.selectedAccounts.map(account => account.id)
      };

      this.shareAccessService.shareAccess(this.invitationData).subscribe({
        next: () => {
          this.processingRequest = false;
          this.router.navigate(['/manage-access'], {queryParams: {sharedAccess: true}}).then();
        },
        error: (error) => {
          this.processingRequest = false;
          if (this.errorService.isInfrastructureError(error)) {
            this.tryAgainLater = true;
          } else {
            this.handleError(error.error);
          }
        }
      });
    }
  }

  isDataValid(): boolean {
    return this.shareAccessForm.valid && this.selectedAccounts && this.selectedAccounts.length > 0;
  }

  private loadAccountOwnerAndFillDropdown(): void {
    this.userService.getUserAssociatedCustomersData().subscribe(data => {
      if (data && data.length !== 0) {
        data.forEach(accountOwnerId => {
          this.accountList.forEach(account => {
            Object.keys(account.accountOwners).forEach(key => {
              if (accountOwnerId === Number(key)) {
                const mappedAccountOwnerIds = this.accountOwnerList.map(accountOwner => accountOwner.id);
                if (mappedAccountOwnerIds.indexOf(accountOwnerId) === -1) {
                  this.populateAccountsForAccountOwner(accountOwnerId, false);
                  if (this.matchingAccounts.length > 0) {
                    const accountOwnerData = {
                      'id': accountOwnerId,
                      'name': account.accountOwners[key].name
                    };
                    this.accountOwnerList.push(accountOwnerData);
                  }
                }
              }
            });
          });
        });

        this.prepopulateAccountOwnersDropdown();
      }
    });
  }

  private loadUserAccounts(): void {
    const accountsData = this.route.snapshot.data.accountsData;

    if (accountsData && accountsData.accounts) {
      this.accountList = accountsData.accounts.map(account => {
        return {
          'id': account.accountId,
          'title': account.accountTitles[0] + ' - ' + account.transactionAccountNumber,
          'accountType': account.accountType,
          'accountOwners': account.accountOwners
        };
      });

      this.prepopulateSharedAccounts();
      this.loadAccountOwnerAndFillDropdown();
    }
  }

  private prepopulateSharedAccounts() {
    if (this.accountId != null) {
      this.accountList.forEach(account => {
        if (account.id === this.accountId) {
          this.selectedAccounts = [account];
        }
      });
    }
  }

  private handleError(error: Error): void {
    if (error.message.search('ERR_INVALID_INVITATION') > -1) {
      this.accountAlreadyShared = true;
    } else if (error.message.search('ERR_SHARE_NOT_ALLOWED') > -1) {
      this.displayEmailNotValidError = true;
    } else if (error.message.search('ERR_USER_INVITATION_DENIED_EXCEPTION') > -1) {
      this.invitationDenied = true;
    } else {
      this.router.navigate([InternalRoutes.ERROR]).then();
    }
  }
}
